const debug = require('debug')('mtk:react:user');

import React, { Fragment, useEffect } from 'react';
import { Map as IMap } from 'immutable';

import { FiArrowUpRight, FiTrash2 } from 'react-icons/fi';

import { roles, roleLabels, noPasswordRoles, userPreferenceOptions } from '../constants';
import { routes } from '../routes';
import { can } from '../can';
import { timeout, formatDateTime } from '../utils';

import api from '../utils/api';
import { loadUnits } from '../utils/api/units';

import { useForm } from '../utils/form';

import { Loading, NotFound } from './PageStatus';

import PageHeader from '../components/common/PageHeader';
import Button from '../components/common/Button';

import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Select, { objectToOptions, indexToOptions } from '../components/common/Select';

import Link from '../components/common/Link';
import { List, Header, Body, Row, Cell } from '../components/common/List';

import grid from '../styles/grid.css';
import css from './User.css';

const userFields = {
  name: ['text'],
  email: ['email', true],
  role: ['text', true],
  password: ['password', false],
  password_confirm: [
    'password',
    false,
    {
      validate: (v, values) => (v == values.get('password') ? null : 'las contraseñas deben coincidir'),
    },
  ],
  phone: ['tel'],
  clientName: ['text'],
  clientId: ['number'],
  status: ['text'],
  segments: ['array'],
  extras: ['json'],
  preferences: userPreferenceOptions.reduce((acc, o) => ({ ...acc, [o.name]: [o.type] }), {}),
  locationId: ['number'],
  unit: {
    id: ['number'],
  },
  nationalId: ['rut'],
};

const User = ({ match, history }) => {
  const { id } = match.params;
  const { user, actions: userActions } = api.auth();
  const { item, actions } = api.users.item(id, { relations: 'all' }, { history });
  const { item: client, actions: clientActions } = api.clients.item(null, null, { lazy: true });

  const { actions: segmentsActions } = api.segments.index(null, { lazy: true });
  const { actions: locationsActions } = api.locations.index(null, { lazy: true });

  const userData = item.data ? item.data.toJS() : {};
  const clientMedicalEnabled = client.data && client.data.getIn(['preferences', 'medical']) === true;
  const form = useForm(userFields, item.data, {
    // The form only holds known preferences, so merge them over the stored ones.
    submit: (data) => {
      const formPreferences = data.get('preferences');
      const storedPreferences = item.data && item.data.get('preferences');
      if (IMap.isMap(formPreferences) && IMap.isMap(storedPreferences)) {
        data = data.set('preferences', storedPreferences.merge(formPreferences));
      }
      return actions.update(data);
    },
  });

  useEffect(() => {
    if (userData.clientId) clientActions.get(null, null, userData.clientId);
  }, [userData.clientId]);

  const getSegments = async (search) => {
    if (!item.loaded) {
      await timeout(200);
      return await getSegments(search);
    }

    const index = await segmentsActions.get({ search, scopeToClientId: userData.clientId, perPage: 50 });
    return indexToOptions(index);
  };

  const onSegmentAdd = (name, value) => {
    actions.segmentAssign(value);
  };

  const onSegmentRemove = async (id) => {
    await actions.segmentUnassign(id);
  };

  const onDelete = async () => {
    await actions.delete();
  };
  const onChangePassword = async () => {
    if (form.getValue('password') == form.getValue('password_confirm')) {
      if (await actions.changePassword(form.getValue('password'))) {
        form.clearValues(['password', 'password_confirm']);
      }
    }
  };

  const getLocations = async (search) => {
    const index = await locationsActions.get({ search, perPage: 50 });
    return indexToOptions(index, { value: 'id', label: 'name' });
  };

  const locationId = form.getValue(['locationId']);

  const getUnits = async (search) => {
    const index = await loadUnits(locationId, { search, perPage: 50 });

    return indexToOptions(index, { value: 'id', label: 'name' });
  };

  const onUnitAdd = async (name, value, option) => {
    await actions.update({ unitId: value });
    await actions.get();
  };

  const resetOpt = async () => {
    const otpReset = await userActions.otpReset({
      id: userData.id,
    });
    window.location.reload(false);
  };

  const isAdmin = user.role == 'admin';
  const readOnly = !can(user, 'update', 'users', userData);

  const lockers =
    item.data && item.data.has('segments')
      ? item.data
        .get('segments')
        .groupBy((s) => s.get('lockerName'))
        .toList()
        .toJS()
      : [];
  const segments =
    item.data && item.data.has('segments')
      ? item.data
        .get('segments')
        .groupBy((s) => s.get('segmentId'))
        .map((s) => s.first())
        .toList()
      : false;

  if (item.status == 404) return <NotFound />;
  if (!item.loaded) return <Loading />;

  return (
    <main>
      <PageHeader
        section="users"
        title={item.loaded ? userData.name || userData.email : '...'}
        dirty={form.dirty}
        autoSave
        {...item}>
        {can(user, 'delete', 'users', userData) && (
          <Button destructive confirm="¿Eliminar este Usuario?" onClick={onDelete}>
            Eliminar
          </Button>
        )}
      </PageHeader>

      <div className={grid.grid}>
        <section className={grid.half}>
          <div className={css.formPanel}>
          <h2 className={css.panelTitle}>Datos personales</h2>
          <Input name="name" label="Nombre" clickToEdit readOnly={readOnly} form={form} />

          <Input
            name="email"
            label="E-Mail"
            clickToEdit
            placeholder="user@maletek.cl"
            type="email"
            readOnly={readOnly}
            form={form}
          />

          <Input name="phone" label="Teléfono Movil" type="tel" readOnly={readOnly} clickToEdit form={form} />

          <Input name="nationalId" label="RUT" readOnly={readOnly} clickToEdit form={form} />
          </div>

          {/* ToDo: Add check if item.data.clientMode = housing when available */}
          {userData.role == roles('resident') && (
            <div className={css.formPanel}>
              <h2 className={css.panelTitle}>Unidad Housing</h2>
              {form.getValue(['unit', 'id']) ? (
                <div className={css.row}>
                  <p>
                    <small>
                      Seleccionada: <strong>{userData.unit?.name}</strong>{' '}
                      {userData.unit?.locationName && (
                        <>
                          {' '}
                          en <strong>{userData.unit.locationName} </strong>
                        </>
                      )}
                    </small>
                  </p>
                  <Button small onClick={() => form.setValue(['unit', 'id'], null)}>
                    Cambiar
                  </Button>
                </div>
              ) : (
                <>
                  <Select
                    readOnly={readOnly}
                    name="locationId"
                    label="Ubicación"
                    isClearable={false}
                    form={form}
                    loadOptions={getLocations}
                    placeholder="Busca una ubicación para elegir unidades"
                  />

                  <Select
                    readOnly={readOnly || !locationId}
                    name={['unit', 'id']}
                    label="Unidad"
                    isClearable={false}
                    onSelectOption={onUnitAdd}
                    form={form}
                    loadOptions={!!locationId ? getUnits : false}
                    placeholder="No asignada"
                  />
                </>
              )}
            </div>
          )}
          {userData.role !== roles('noAccess') && (
            <div className={css.formPanel}>
          <h2 className={css.panelTitle}>Acceso a Lockers</h2>
          {roles('admin') == userData.role ? (
            <p className={css.infoText}>Usuario administrador tiene acceso a todos los lockers del sistema.</p>
          ) : roles('client') == userData.role ? (
            <p className={css.infoText}>
              Usuario administrador de cliente tiene acceso a todos los lockers a los que el cliente tiene acceso.
            </p>
          ) : (
            <Fragment>
              {can(user, 'update', 'segments') ? (
                <Select
                  isMulti
                  readOnly={readOnly}
                  isClearable={false}
                  hideValues
                  label={'Agregar a grupo'}
                  name={'segments'}
                  value={
                    segments
                      ? indexToOptions(segments, {
                        value: 'segmentId',
                        label: 'segmentName',
                      })
                      : ''
                  }
                  onChange={() => { }}
                  onSelectOption={onSegmentAdd}
                  onDeselectOption={onSegmentRemove}
                  loadOptions={getSegments}
                  noOptionsMessage={(a) => {
                    return a.inputValue ? `No hay grupos conteniendo "${a.inputValue}"` : 'No hay grupos por agregar.';
                  }}
                />
              ) : null}
              <List>
                <Header>
                  <Cell head>Locker</Cell>
                  <Cell head>{can(user, 'update', 'segments') ? 'Grupo' : null}</Cell>
                </Header>
                <Body>
                  {lockers.length ? (
                    lockers.map((locker, i) => (
                      <Row key={`locker-${i}`}>
                        <Cell top>
                          <Link to={routes('locker', { id: locker.lockerId })}>{locker[0].lockerName}</Link>
                        </Cell>
                        <Cell top>
                          {locker.map((seg, j) =>
                            can(user, 'update', 'segments') ? (
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                                key={`segment-${j}`}>
                                {seg.segmentName}

                                <div>
                                  <Link
                                    style={{ fontSize: 'var(--font-size-small)' }}
                                    inline
                                    tooltip={'editar grupo'}
                                    to={routes('segment', { id: seg.segmentId })}>
                                    <FiArrowUpRight />
                                  </Link>
                                  {' | '}
                                  <Link
                                    style={{ fontSize: 'var(--font-size-small)' }}
                                    inline
                                    tooltip={'remover usuario de este grupo'}
                                    onClick={() => onSegmentRemove(seg.segmentId)}>
                                    <FiTrash2 />
                                  </Link>
                                </div>
                              </div>
                            ) : null
                          )}
                        </Cell>
                      </Row>
                    ))
                  ) : (
                    <Row>
                      <Cell>Este usuario no tiene acceso a lockers.</Cell>
                    </Row>
                  )}
                </Body>
              </List>
            </Fragment>
          )}
            </div>
          )}
        </section>
        <section className={grid.half}>
          <div className={css.formPanel}>
          <h2 className={css.panelTitle}>Cuenta</h2>
          <Input readOnly name="clientName" label="Cliente" form={form} />
          <Input
            readOnly
            name="role"
            label="Rol"
            onChange={() => { }}
            value={
              clientMedicalEnabled && userData.role === roles('resident')
                ? '—'
                : userData.role === roles('noAccess') && clientMedicalEnabled
                ? 'Paciente'
                : userData.role
                  ? roleLabels(userData.role)
                  : ''
            }
          />

          <Select
            label="Estado"
            name="status"
            options={objectToOptions({ active: 'Activo', inactive: 'Inactivo' })}
            isClearable={false}
            readOnly={readOnly}
            form={form}
          />

          <div className={css.meta}>
            <div className={css.row}>
              <strong>Fecha de creación</strong>
              <span>{formatDateTime(userData.createdAt) || 'No disponible'}</span>
            </div>
            <div className={css.row}>
              <strong>Fecha de actualización</strong>
              <span>{formatDateTime(userData.updatedAt) || 'No disponible'}</span>
            </div>
          </div>
          </div>

          {clientMedicalEnabled && userData.role === roles('noAccess') && (
            <div className={css.formPanel}>
              <h2 className={css.panelTitle}>Datos médicos adicionales</h2>
              {userPreferenceOptions.filter((option) => !option.isMedical || clientMedicalEnabled).map((option) => (
                <div key={option.name} className={css.extraItem}>
                  {option.type == 'toggle' ? (
                    <Toggle
                      name={['preferences', option.name]}
                      label={option.label}
                      readOnly={readOnly}
                      form={form}
                    />
                  ) : (
                    <Input
                      name={['preferences', option.name]}
                      label={option.label}
                      placeholder={option.placeholder}
                      readOnly={readOnly}
                      form={form}
                    />
                  )}
                  {option.note && <p className={css.infoText}>{option.note}</p>}
                </div>
              ))}
            </div>
          )}

          {(!noPasswordRoles.has(userData.role) || (item.loaded && userData.client2fa)) && (
          <div className={css.formPanel}>
          <h2 className={css.panelTitle}>Seguridad</h2>
          {!noPasswordRoles.has(userData.role) && (
            <>
              <h3 className={css.panelSubtitle}>Cambio de Contraseña</h3>
              <Input
                name="password"
                label={'Nueva contraseña'}
                placeholder={'•••••••••'}
                type="password"
                skipSubmit
                readOnly={readOnly}
                form={form}
              />
            </>
          )}
          {form.getValue('password') && (
            <Input
              name="password_confirm"
              label={'Confirmar contraseña'}
              placeholder={'•••••••••'}
              type="password"
              skipSubmit
              readOnly={readOnly}
              form={form}
            />
          )}
          {form.getValue('password') && (
            <Button
              disabled={form.getValue('password') !== form.getValue('password_confirm')}
              onClick={onChangePassword}>
              Cambiar contraseña
            </Button>
          )}

          {item.loaded && userData.client2fa && (
            <Fragment>
              <h3 className={css.panelSubtitle}>Doble autenticación</h3>
              <p className={css.infoText}>
                {userData.otpValidated ? 'Resetear código para doble autenticación' : 'Aún no verifica 2FA'}
              </p>

              <Button
                disabled={!isAdmin || !userData.otpValidated}
                confirm="¿Esta seguro de crear el nuevo código?"
                onClick={resetOpt}>
                Reset OTP
              </Button>
            </Fragment>
          )}
          </div>
          )}
        </section>
      </div>
    </main>
  );
};

export default User;
